// UC91-03 / UC91-04 — what identifies one line of the chart and one group of rows
// of the list. Outside the geographic levels a series is a medicine; under them it
// is a medicine in one region or locality (docs/shape-uc-91-geographic-aggregation.md §2),
// so the medicine code alone no longer tells two series apart.
import type { TimeseriesPoint, TimeseriesSeries } from './useStockTimeseries';

export function seriesKey(series: Pick<TimeseriesSeries, 'medicineCode' | 'groupId'>): string {
  return `${series.medicineCode}|${series.groupId}`;
}

// The place a series is about, as the reader sees it. Stock whose site has no
// region or locality on record is a group like any other (the groups add up to the
// national figure), named `unassigned`; a medicine with no stock at all is an empty
// series with no place to name.
export function placeOf(series: TimeseriesSeries, unassigned: string): string {
  if (series.groupLabel.length > 0) return series.groupLabel;
  return series.points.length > 0 ? unassigned : '';
}

// UC91-08 / UC91-09: a point was reported, carried from the last level reported (a
// bucket with no report of its own) or projected past today. The chart and the list
// both say which, from the two flags the API sends (docs/shape-uc-91-time-window.md §2).
export type PointKind = 'reported' | 'carried' | 'projected';

export function pointKind(
  point: Pick<TimeseriesPoint, 'isProjected' | 'isCarryForward'>,
): PointKind {
  if (point.isProjected) return 'projected';
  return point.isCarryForward ? 'carried' : 'reported';
}

// CF-66: the chart draws at most this many series at once; every series stays in the
// table (docs/shape-uc-91-series-and-movements.md §2 D5).
export const CHART_SERIES_LIMIT = 8;

// The series on the chart: the first CHART_SERIES_LIMIT the reader has not hidden, in
// the order they are listed, which is the order the medicines were picked in.
export function chartedKeys(
  series: Pick<TimeseriesSeries, 'medicineCode' | 'groupId'>[],
  hidden: ReadonlySet<string>,
): Set<string> {
  return new Set(
    series
      .map(seriesKey)
      .filter((key) => !hidden.has(key))
      .slice(0, CHART_SERIES_LIMIT),
  );
}

// CF-66 / WCAG 1.4.1: besides its colour, each drawn series has a marker of its own,
// taken by its position among the series drawn: eight shapes for the eight on the
// chart, so no two of them share one.
export type MarkerShape =
  | 'circle'
  | 'square'
  | 'triangle'
  | 'diamond'
  | 'triangle-down'
  | 'plus'
  | 'cross'
  | 'star';

const MARKER_SHAPES: readonly MarkerShape[] = [
  'circle',
  'square',
  'triangle',
  'diamond',
  'triangle-down',
  'plus',
  'cross',
  'star',
];

export function markerOf(index: number): MarkerShape {
  return MARKER_SHAPES[index % MARKER_SHAPES.length] ?? 'circle';
}
